import React, { useState, useEffect, useRef } from 'react'
import { useTenant } from '../../contexts/TenantContext'
import { useAuth } from '../../contexts/AuthContext'
import { buildApiUrl } from '../../config/api'
import { MaleIcon, FemaleIcon } from '../common/GenderIcon'

const CompactEventForm = ({ event = null, onSubmit, onCancel, isLoading = false, onSuccess = null }) => {
  const { tenant } = useTenant()
  const { token } = useAuth()
  const [formData, setFormData] = useState({
    event_name: '',
    event_organizer: '',
    event_venue: '',
    event_city: '',
    event_state: '',
    event_start_date: '',
    event_end_date: '',
    event_type: 'N',
    event_description: '',
    contact_info: '',
    coordinator_name: '',
    coordinator_contact: '',
    is_published: false,
    is_featured: false
  })

  const [errors, setErrors] = useState({})
  const [registeredUsers, setRegisteredUsers] = useState([])

  // Category master data and selections
  const [masterCategories, setMasterCategories] = useState([])
  const [categorySelections, setCategorySelections] = useState({})
  // Format: { categoryId: { male: true/false, female: true/false } }

  // Get today's date in YYYY-MM-DD format for min date validation
  const getTodayDate = () => {
    const today = new Date()
    return today.toISOString().split('T')[0]
  }

  // Earliest allowed start date for a new event: tomorrow
  // (an event created today can only start from the next day).
  const getTomorrowDate = () => {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    return d.toISOString().split('T')[0]
  }

  // Helper function to format date to YYYY-MM-DD
  const formatDateForInput = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return ''
    return date.toISOString().split('T')[0]
  }

  // Load master categories and registered users on mount or when event changes
  useEffect(() => {
    loadMasterCategories()
    loadRegisteredUsers()
  }, [event?.id])

  const loadRegisteredUsers = async () => {
    try {
      console.log('Loading registered users from:', buildApiUrl('/client-admin/users/dropdown'))
      const response = await fetch(buildApiUrl('/client-admin/users/dropdown'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'X-Original-Host': window.location.hostname
        }
      })
      console.log('Response status:', response.status)
      if (response.ok) {
        const data = await response.json()
        console.log('Registered users data:', data)
        setRegisteredUsers(data.data || [])
      } else {
        console.error('Failed to load users:', response.status, await response.text())
      }
    } catch (error) {
      console.error('Error loading registered users:', error)
    }
  }

  const loadMasterCategories = async () => {
    try {
      const response = await fetch(buildApiUrl('/client-admin/categories'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      })
      if (response.ok) {
        const data = await response.json()
        setMasterCategories(data.data || [])

        // Initialize selections for all categories
        // If editing an event, preserve existing selections from event.allowed_categories
        const initialSelections = {}
        ;(data.data || []).forEach(cat => {
          initialSelections[cat.id] = { male: false, female: false }
        })

        // If we have an event with existing categories, merge them
        if (event?.allowed_categories) {
          event.allowed_categories.forEach(cat => {
            initialSelections[cat.id] = {
              male: cat.genders?.includes('M') || false,
              female: cat.genders?.includes('F') || false
            }
          })
        }

        setCategorySelections(initialSelections)
      }
    } catch (error) {
      console.error('Error loading categories:', error)
    }
  }

  useEffect(() => {
    if (event) {
      setFormData({
        event_name: event.event_name || '',
        event_organizer: event.event_organizer || '',
        event_venue: event.event_venue || '',
        event_city: event.event_city || '',
        event_state: event.event_state || '',
        event_start_date: formatDateForInput(event.event_start_date),
        event_end_date: formatDateForInput(event.event_end_date),
        event_type: event.event_type || 'N',
        event_description: event.event_description || '',
        contact_info: event.contact_info || '',
        coordinator_name: event.coordinator_name || '',
        coordinator_contact: event.coordinator_contact || '',
        is_published: event.is_published || false,
        is_featured: event.is_featured || false
      })

      // If editing, load existing category selections
      if (event.allowed_categories) {
        const selections = {}
        event.allowed_categories.forEach(cat => {
          selections[cat.id] = {
            male: cat.genders?.includes('M') || false,
            female: cat.genders?.includes('F') || false
          }
        })
        setCategorySelections(prev => ({ ...prev, ...selections }))
      }
    }
  }, [event])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target

    // For coordinator contact field, only allow digits and limit to 10
    if (name === 'coordinator_contact') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10)
      setFormData(prev => ({ ...prev, [name]: digitsOnly }))
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }))
      }
      return
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  // Handle category checkbox change
  const handleCategoryCheckboxChange = (categoryId, gender) => {
    setCategorySelections(prev => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        [gender]: !prev[categoryId]?.[gender]
      }
    }))

    // Clear error when user changes category
    if (errors.categories) {
      setErrors(prev => ({ ...prev, categories: '' }))
    }
  }

  // Select all genders for a category
  const handleSelectAllForCategory = (categoryId) => {
    const current = categorySelections[categoryId] || {}
    const allSelected = current.male && current.female

    setCategorySelections(prev => ({
      ...prev,
      [categoryId]: {
        male: !allSelected,
        female: !allSelected
      }
    }))
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.event_name.trim()) newErrors.event_name = 'Event name is required'
    if (!formData.event_organizer.trim()) newErrors.event_organizer = 'Organizer is required'
    if (!formData.event_venue.trim()) newErrors.event_venue = 'Venue is required'
    if (!formData.event_start_date) newErrors.event_start_date = 'Start date is required'
    else if (formData.event_start_date < getTomorrowDate()) {
      newErrors.event_start_date = 'Start date must be from tomorrow onwards'
    }
    if (!formData.event_end_date) newErrors.event_end_date = 'End date is required'
    else if (formData.event_start_date && formData.event_end_date < formData.event_start_date) {
      newErrors.event_end_date = 'End date cannot be before start date'
    }

    // Check if at least one category+gender is selected
    const hasSelectedCategory = Object.values(categorySelections).some(
      sel => sel.male || sel.female
    )
    if (!hasSelectedCategory) {
      newErrors.categories = 'Please select at least one category and gender'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    const submitData = new FormData()
    Object.keys(formData).forEach(key => {
      if (formData[key] !== '' && formData[key] !== null) {
        // Convert boolean to 1/0 for Laravel validation
        const value = typeof formData[key] === 'boolean' ? (formData[key] ? 1 : 0) : formData[key]
        submitData.append(key, value)
      }
    })

    // Build category selections array for backend
    // Format: [{ category_id: 1, gender: 'M' }, { category_id: 1, gender: 'F' }, ...]
    const tournamentCategories = []
    Object.entries(categorySelections).forEach(([categoryId, genders]) => {
      if (genders.male) {
        tournamentCategories.push({ category_id: parseInt(categoryId), gender: 'M' })
      }
      if (genders.female) {
        tournamentCategories.push({ category_id: parseInt(categoryId), gender: 'F' })
      }
    })
    submitData.append('tournament_categories', JSON.stringify(tournamentCategories))

    // Call the onSubmit handler
    const result = await onSubmit(submitData)

    // Call onSuccess callback if provided
    if (result !== false && onSuccess) {
      onSuccess()
    }
  }

  return (
    <div className="compact-event-form">
      <div className="form-container">
        <div className="form-header">
        <h2 style={{ margin: 0 }}>
          <i className="fas fa-calendar-plus"></i>
          {event ? 'Edit Event' : 'Create New Event'}
        </h2>
        <button
          type="button"
          className="back-btn"
          onClick={onCancel}
          title="Back to tournaments"
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 16px', borderRadius: '8px', border: 'none',
            background: '#c00f15', color: '#ffffff', fontWeight: 600,
            fontSize: '0.9rem', cursor: 'pointer'
          }}
        >
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="event-form">
        {/* Basic Info - 2 columns */}
        <div className="form-row">
          <div className="form-group">
            <label>Event Name *</label>
            <input
              type="text"
              name="event_name"
              value={formData.event_name}
              onChange={handleChange}
              className={errors.event_name ? 'error' : ''}
              placeholder="Enter event name"
            />
            {errors.event_name && <span className="error-text">{errors.event_name}</span>}
          </div>
          
          <div className="form-group">
            <label>Organizer *</label>
            <select
              name="event_organizer"
              value={formData.event_organizer}
              onChange={handleChange}
              className={errors.event_organizer ? 'error' : ''}
            >
              <option value="">Select Organizer</option>
              {registeredUsers.map(user => (
                <option key={user.id} value={user.name}>{user.name}</option>
              ))}
            </select>
            {errors.event_organizer && <span className="error-text">{errors.event_organizer}</span>}
          </div>
        </div>

        {/* Tournament Coordinator */}
        <div className="form-row">
          <div className="form-group">
            <label>Tournament Coordinator Name</label>
            <input
              type="text"
              name="coordinator_name"
              value={formData.coordinator_name}
              onChange={handleChange}
              placeholder="Enter coordinator name"
            />
          </div>

          <div className="form-group">
            <label>Coordinator Contact Number</label>
            <input
              type="tel"
              name="coordinator_contact"
              value={formData.coordinator_contact}
              onChange={handleChange}
              placeholder="Enter 10-digit mobile number"
              pattern="[0-9]{10}"
              maxLength="10"
            />
            <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>Enter 10-digit mobile number (digits only)</small>
          </div>
        </div>

        {/* Location */}
        <div className="form-row">
          <div className="form-group">
            <label>Venue *</label>
            <input
              type="text"
              name="event_venue"
              value={formData.event_venue}
              onChange={handleChange}
              className={errors.event_venue ? 'error' : ''}
              placeholder="Event venue"
            />
            {errors.event_venue && <span className="error-text">{errors.event_venue}</span>}
          </div>
          
          <div className="form-group">
            <label>City</label>
            <input
              type="text"
              name="event_city"
              value={formData.event_city}
              onChange={handleChange}
              placeholder="City"
            />
          </div>
        </div>

        {/* Dates */}
        <div className="form-row">
          <div className="form-group">
            <label>Start Date *</label>
            <input
              type="date"
              name="event_start_date"
              value={formData.event_start_date}
              onChange={handleChange}
              className={errors.event_start_date ? 'error' : ''}
              min={getTomorrowDate()}
            />
            {errors.event_start_date && <span className="error-text">{errors.event_start_date}</span>}
          </div>

          <div className="form-group">
            <label>End Date *</label>
            <input
              type="date"
              name="event_end_date"
              value={formData.event_end_date}
              onChange={handleChange}
              className={errors.event_end_date ? 'error' : ''}
              min={formData.event_start_date || getTomorrowDate()}
            />
            {errors.event_end_date && <span className="error-text">{errors.event_end_date}</span>}
          </div>
        </div>

        {/* Event Type */}
        <div className="form-row">
          <div className="form-group">
            <label>Event Type</label>
            <select
              name="event_type"
              value={formData.event_type}
              onChange={handleChange}
            >
              <option value="N">National</option>
            </select>
          </div>
        </div>

        {/* Category & Gender Selection */}
        <div className="form-group">
          <label style={{ marginBottom: '12px', display: 'block' }}>
            Select Categories & Gender *
            <span style={{ fontWeight: '400', fontSize: '0.85rem', color: '#6b7280', marginLeft: '8px' }}>
              (Choose which categories and genders are allowed in this tournament)
            </span>
          </label>

          <div style={{
            background: '#f8fafc',
            border: '2px solid #e2e8f0',
            borderRadius: '12px',
            padding: '16px',
            borderColor: errors.categories ? '#ef4444' : '#e2e8f0'
          }}>
            {masterCategories.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                Loading categories...
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '12px' }}>
                {masterCategories.map(category => (
                  <div
                    key={category.id}
                    style={{
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      padding: '14px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      transition: 'all 0.2s',
                      borderColor: (categorySelections[category.id]?.male || categorySelections[category.id]?.female)
                        ? '#3b82f6' : '#e2e8f0',
                      boxShadow: (categorySelections[category.id]?.male || categorySelections[category.id]?.female)
                        ? '0 2px 8px rgba(59, 130, 246, 0.15)' : 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div
                        onClick={() => handleSelectAllForCategory(category.id)}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: (categorySelections[category.id]?.male && categorySelections[category.id]?.female)
                            ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
                            : (categorySelections[category.id]?.male || categorySelections[category.id]?.female)
                              ? 'linear-gradient(135deg, #93c5fd, #60a5fa)'
                              : '#f1f5f9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        <i
                          className={
                            (categorySelections[category.id]?.male && categorySelections[category.id]?.female)
                              ? 'fas fa-check'
                              : (categorySelections[category.id]?.male || categorySelections[category.id]?.female)
                                ? 'fas fa-minus'
                                : 'fas fa-layer-group'
                          }
                          style={{
                            color: (categorySelections[category.id]?.male || categorySelections[category.id]?.female)
                              ? 'white' : '#64748b',
                            fontSize: '14px'
                          }}
                        ></i>
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '0.95rem' }}>
                          {category.name}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                          {category.age_range}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                      {/* Male Checkbox */}
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 14px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          background: categorySelections[category.id]?.male ? '#dbeafe' : '#f8fafc',
                          border: categorySelections[category.id]?.male ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                          transition: 'all 0.2s'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={categorySelections[category.id]?.male || false}
                          onChange={() => handleCategoryCheckboxChange(category.id, 'male')}
                          style={{ display: 'none' }}
                        />
                        <MaleIcon
                          style={{
                            color: categorySelections[category.id]?.male ? '#2563eb' : '#9ca3af',
                            fontSize: '14px'
                          }}
                        />
                        <span style={{
                          fontSize: '0.85rem',
                          fontWeight: categorySelections[category.id]?.male ? '600' : '500',
                          color: categorySelections[category.id]?.male ? '#1e40af' : '#64748b'
                        }}>
                          Male
                        </span>
                        {categorySelections[category.id]?.male && (
                          <i className="fas fa-check" style={{ color: '#2563eb', fontSize: '12px' }}></i>
                        )}
                      </label>

                      {/* Female Checkbox */}
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 14px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          background: categorySelections[category.id]?.female ? '#fce7f3' : '#f8fafc',
                          border: categorySelections[category.id]?.female ? '2px solid #ec4899' : '2px solid #e2e8f0',
                          transition: 'all 0.2s'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={categorySelections[category.id]?.female || false}
                          onChange={() => handleCategoryCheckboxChange(category.id, 'female')}
                          style={{ display: 'none' }}
                        />
                        <FemaleIcon
                          style={{
                            color: categorySelections[category.id]?.female ? '#db2777' : '#9ca3af',
                            fontSize: '14px'
                          }}
                        />
                        <span style={{
                          fontSize: '0.85rem',
                          fontWeight: categorySelections[category.id]?.female ? '600' : '500',
                          color: categorySelections[category.id]?.female ? '#9d174d' : '#64748b'
                        }}>
                          Female
                        </span>
                        {categorySelections[category.id]?.female && (
                          <i className="fas fa-check" style={{ color: '#db2777', fontSize: '12px' }}></i>
                        )}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {errors.categories && <span className="error-text">{errors.categories}</span>}
        </div>

        {/* Description */}
        <div className="form-group">
          <label>Description</label>
          <textarea
            name="event_description"
            value={formData.event_description}
            onChange={handleChange}
            placeholder="Event description..."
            rows="3"
          ></textarea>
        </div>

        {/* Contact Info */}
        <div className="form-group">
          <label>Contact Information</label>
          <input
            type="text"
            name="contact_info"
            value={formData.contact_info}
            onChange={handleChange}
            placeholder="Contact details"
          />
        </div>

        {/* Publish Checkbox */}
        <div className="form-group publish-checkbox">
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', padding: '0.75rem', background: formData.is_published ? '#dcfce7' : '#f8fafc', borderRadius: '8px', border: formData.is_published ? '2px solid #22c55e' : '2px solid #e2e8f0' }}>
            <input
              type="checkbox"
              name="is_published"
              checked={formData.is_published}
              onChange={handleChange}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={{ fontWeight: '500', color: formData.is_published ? '#166534' : '#475569' }}>
              <i className={`fas ${formData.is_published ? 'fa-globe' : 'fa-eye-slash'}`} style={{ marginRight: '0.5rem' }}></i>
              {formData.is_published ? 'Published - Visible to Users' : 'Draft - Not visible to Users'}
            </span>
          </label>
          <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
            Enable this to make the tournament visible to all registered users
          </small>
        </div>

        {/* Submit Buttons */}
        <div className="form-actions">
          <button type="button" className="cancel-btn" onClick={onCancel}>
            <i className="fas fa-times"></i>
            Cancel
          </button>
          <button type="submit" className="submit-btn" disabled={isLoading}>
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                {event ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <i className="fas fa-check"></i>
                {event ? 'Update Event' : 'Create Event'}
              </>
            )}
          </button>
        </div>
      </form>
      </div>
    </div>
  )
}

export default CompactEventForm